import React, { useState } from "react";
import kamlaLogo from '../assests/kamlaLogo.png';
import '../styles/main.css';

// --- Utility Functions ---

function numberToWords(num) {
  if (num == null) return "-";
  num = Number(num);
  if (isNaN(num)) return "-";

  const ones = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
    'Seventeen', 'Eighteen', 'Nineteen'
  ];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty',
    'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const scales = ['', 'Thousand', 'Lakh', 'Crore'];

  function chunkToWords(n) {
    let word = "";
    if (n > 99) {
      word += ones[Math.floor(n / 100)] + " Hundred";
      n = n % 100;
      if (n) word += " and ";
    }
    if (n < 20) {
      word += ones[n];
    } else {
      word += tens[Math.floor(n / 10)];
      if (n % 10) word += " " + ones[n % 10];
    }
    return word;
  }

  function splitIndianNumber(n) {
    const str = n.toString();
    let chunks = [];
    let end = str.length;
    chunks.push(parseInt(str.substring(Math.max(0, end - 3), end), 10));
    end -= 3;
    while (end > 0) {
      let start = Math.max(0, end - 2);
      chunks.push(parseInt(str.substring(start, end), 10));
      end -= 2;
    }
    return chunks;
  }

  if (num === 0) return "Zero";
  const numberChunks = splitIndianNumber(parseInt(num));

  let wordArray = [];
  for (let i = 0; i < numberChunks.length; i++) {
    if (numberChunks[i] > 0) {
      wordArray.push(chunkToWords(numberChunks[i]) + (scales[i] ? " " + scales[i] : ""));
    }
  }
  return wordArray.reverse().join(" ").trim();
}

function formatDateToDDMMYYYY(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date)) return "";
  const day = ("0" + date.getDate()).slice(-2);
  const month = ("0" + (date.getMonth() + 1)).slice(-2);
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

const formatIndianTime = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toLocaleTimeString("en-IN", {
        hour: "2-digit", 
        minute: "2-digit",
        hour12: true
      });
    }
  } catch (e) {
    return "";
  }
  return "";
};

const CustomerBillingReceipt = ({
  show,
  receiptData,
  companyDetails,
  onClose,
  onPrint,
}) => {
  const [showHeader, setShowHeader] = useState(true);

  if (!show || !receiptData || !companyDetails) return null;

  const handlePrint = () => {
    if (onPrint) onPrint();
    else window.print();
  };

  const handleToggleHeader = () => {
    setShowHeader(!showHeader);
  };

  // --- Data Mapping ---
  const { customer, summary, details } = receiptData;

  const safeSummary = summary || {};
  const safeCustomer = customer || {};
  const safeDetails = Array.isArray(details) ? details : [];

  const billDate = safeSummary.billDate || (safeDetails[0]?.billDate) || new Date();
  const receiptDateStr = formatDateToDDMMYYYY(billDate);
  const receiptTimeStr = formatIndianTime(billDate);

  // Financials
  const totalAmount = Number(safeSummary.total) || 0;
  const discountAmount = Number(safeSummary.dis) || 0; 
  const payableAmount = totalAmount - discountAmount; 
  const receivedAmount = Number(safeSummary.received) || 0;
  const dueAmount = Number(safeSummary.due) || 0;

  // Process Items for Pagination
  const allItems = safeDetails.map((item, index) => ({
    sr: index + 1,
    description: item.services || "Hospital Services",
    qty: Number(item.qty) || 1,
    rate: Number(item.rate) || 0,
    amount: Number(item.amount) || 0,
  }));

  // ✅ LOGIC: PAGINATION & PAGE TOTALS (20 ITEMS PER PAGE)
  const ITEMS_PER_PAGE = 20; 
  const pages = [];
  let runningTotal = 0;

  for (let i = 0; i < allItems.length; i += ITEMS_PER_PAGE) {
    const pageItems = allItems.slice(i, i + ITEMS_PER_PAGE);
    const pageTotal = pageItems.reduce((sum, item) => sum + item.amount, 0);
    
    pages.push({
      items: pageItems,
      broughtForward: runningTotal, 
      pageTotal: pageTotal,         
      carriedForward: runningTotal + pageTotal 
    });

    runningTotal += pageTotal;
  }

  // Fallback if no items
  if (pages.length === 0) {
    pages.push({ items: [], broughtForward: 0, pageTotal: 0, carriedForward: 0 });
  }

  return (
    <>
      {/* =========================================
         INLINE CSS: PRINT & MARG BILLING STYLES (A4 SIZE)
      ========================================= */}
      <style>{`
        :root {
          --marg-border: 1px solid #000;
          --marg-border-thick: 2px solid #000;
          --marg-font: Arial, Helvetica, sans-serif;
        }

        .marg-receipt-wrapper {
          font-family: var(--marg-font);
          color: #000;
          background: #fff;
          font-size: 12px;
        }

        /* ✅ FIX: Ensure padding and borders don't break the percentage widths */
        .marg-receipt-wrapper * {
          box-sizing: border-box;
        }

        .marg-outer-box {
          border: var(--marg-border-thick);
          margin-bottom: 20px;
          height: 265mm; /* FIXED HEIGHT FOR A4 FULL PAGE */
          max-height: 265mm;
          box-sizing: border-box;
          position: relative;
          background: #fff;
          display: flex;
          flex-direction: column;
          page-break-inside: avoid;
        }

        .marg-header {
          border-bottom: var(--marg-border-thick);
          padding: 8px 10px;
          text-align: center;
          position: relative;
        }
        
        .marg-title-strip {
          background-color: #e0e0e0 !important; 
          border-bottom: var(--marg-border-thick);
          text-align: center;
          padding: 4px;
          font-weight: 900;
          font-size: 14px;
          letter-spacing: 1px;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        .marg-patient-box {
          display: flex;
          border-bottom: var(--marg-border-thick);
        }

        .marg-patient-col { padding: 5px 10px; }
        
        /* ✅ ALIGNMENT FIX: 60% Left Side */
        .marg-patient-left { width: 60%; border-right: var(--marg-border-thick); }
        /* ✅ ALIGNMENT FIX: 40% Right Side */
        .marg-patient-right { width: 40%; }

        .marg-patient-col table {
          width: 100%;
          font-size: 12px;
          line-height: 1.4;
        }
        .marg-patient-col table td { vertical-align: top; }

        /* Items Table */
        .marg-table-container {
          flex-grow: 1; /* PUSHES FOOTER TO BOTTOM */
          display: flex;
          flex-direction: column;
        }

        /* ✅ FIX: Width 100% ensures flex containers act uniformly */
        .marg-table-header {
          display: flex;
          width: 100%;
          border-bottom: var(--marg-border);
          font-weight: bold;
          text-transform: uppercase;
          font-size: 11px;
          background-color: #e0e0e0 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        .marg-table-row {
          display: flex;
          width: 100%;
          font-size: 12px;
        }

        /* ✅ FIX: align-items: stretch forces the internal divs to reach the bottom */
        .marg-table-filler {
          flex-grow: 1;
          display: flex;
          width: 100%;
          align-items: stretch;
        }

        /* ✅ ALIGNMENT FIX: 6% + 54% = 60% (Matches Patient Box & Footer Left Side) */
        .marg-col-sr   { width: 6%;  text-align: center; padding: 4px 5px; border-right: var(--marg-border); }
        .marg-col-desc { width: 54%; text-align: left;   padding: 4px 5px; border-right: var(--marg-border); }
        
        /* ✅ ALIGNMENT FIX: 10% + 12% + 18% = 40% (Matches Patient Box & Footer Right Side) */
        .marg-col-qty  { width: 10%; text-align: center; padding: 4px 5px; border-right: var(--marg-border); }
        .marg-col-rate { width: 12%; text-align: right;  padding: 4px 5px; border-right: var(--marg-border); }
        .marg-col-amt  { width: 18%; text-align: right;  padding: 4px 5px; }

        /* Page Total Row */
        .marg-page-total {
          display: flex;
          width: 100%;
          border-top: var(--marg-border);
          border-bottom: var(--marg-border-thick);
          font-weight: bold;
          font-size: 12px;
          background-color: #f9f9f9 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        /* Footer Sections */
        .marg-footer-grid {
          display: flex;
          border-top: var(--marg-border-thick);
        }

        .marg-words-box {
          width: 60%; /* ✅ Matches left column alignment */
          border-right: var(--marg-border-thick);
          padding: 10px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .marg-calc-box { width: 40%; } /* ✅ Matches right column alignment */
        .marg-calc-table { width: 100%; border-collapse: collapse; }
        .marg-calc-table td { padding: 4px 10px; border-bottom: var(--marg-border); font-size: 12px; }
        .marg-calc-table tr:last-child td { border-bottom: none; }

        .marg-highlight-row td {
          background-color: #e0e0e0 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        /* Signatures - Fixed positioning at bottom */
        .marg-signature-box {
          display: flex;
          justify-content: space-between;
          padding: 35px 20px 10px 20px;
          border-top: var(--marg-border-thick);
          background: #fff;
        }

        .marg-sig-line {
          border-top: 1px solid #000;
          padding-top: 5px;
          text-align: center;
          font-weight: bold;
          font-size: 11px;
          width: 100%;
        }

        /* =========================================
           PRINT MEDIA QUERIES 
        ========================================= */
        @media print {
          @page { size: A4 portrait; margin: 5mm; }
          html, body {
            height: auto !important;
            overflow: visible !important;
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          body * { visibility: hidden; }
          #marg-print-section, #marg-print-section * { visibility: visible; }
          #marg-print-section {
            position: absolute;
            left: 0; top: 0; width: 100%; margin: 0; padding: 0;
          }
          .modal, .modal-dialog, .modal-content {
            border: none !important; box-shadow: none !important;
            background: transparent !important; padding: 0 !important;
          }
          .no-print { display: none !important; }
          .page-break {
            page-break-after: always;
            border-bottom: none !important; margin-bottom: 0 !important;
          }
        }
      `}</style>

      <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.6)" }}>
        <div className="modal-dialog modal-lg" style={{ maxWidth: '950px', margin: '20px auto' }}>
          <div className="modal-content border-0">
            
            <div id="marg-print-section" className="marg-receipt-wrapper p-2 bg-white">
              
              {pages.map((pageData, pageIndex) => (
                <div key={pageIndex} className={`marg-outer-box ${pageIndex < pages.length - 1 ? 'page-break' : ''}`}>
                  
                  {/* --- Header --- */}
                  {showHeader ? (
                    <div className="marg-header">
                      {kamlaLogo && (
                        <img 
                          src={kamlaLogo} 
                          alt="Logo" 
                          style={{ position: 'absolute', left: '15px', top: '10px', width: '70px', height: '70px', objectFit: 'contain' }} 
                        />
                      )}
                      <h2 style={{ margin: '0 0 5px 0', fontWeight: '900', fontSize: '22px', textTransform: 'uppercase' }}>
                        {companyDetails.company_Name}
                      </h2>
                      <p style={{ margin: '0', fontSize: '13px' }}>{companyDetails.address}</p>
                      <p style={{ margin: '2px 0 0 0', fontSize: '12px' }}>
                        <strong>Ph:</strong> {companyDetails.contact1} &nbsp;|&nbsp; <strong>Email:</strong> {companyDetails.email_ID}
                      </p>
                      {companyDetails.reg_No && (
                        <p style={{ margin: '2px 0 0 0', fontSize: '11px' }}><strong>Reg No:</strong> {companyDetails.reg_No}</p>
                      )}
                      {pages.length > 1 && (
                        <div style={{ position: 'absolute', right: '15px', top: '10px', fontSize: '11px', fontWeight: 'bold' }}>
                          Page {pageIndex + 1} of {pages.length}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ height: "100px", position: "relative" }}>
                       {pages.length > 1 && (
                        <div style={{ position: 'absolute', right: '15px', top: '10px', fontSize: '11px', fontWeight: 'bold' }}>
                          Page {pageIndex + 1} of {pages.length}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="marg-title-strip">
                    BILL RECEIPT
                  </div>

                  {/* --- Patient Info Box --- */}
                  <div className="marg-patient-box">
                    <div className="marg-patient-col marg-patient-left">
                      <table>
                        <tbody>
                          <tr>
                            <td width="30%"><strong>Name</strong></td>
                            <td width="5%">:</td>
                            <td><strong style={{ fontSize: '14px', textTransform: 'uppercase' }}>{safeCustomer.custName || "-"}</strong></td>
                          </tr>
                          <tr>
                            <td><strong>Mobile No.</strong></td>
                            <td>:</td>
                            <td>{safeCustomer.mobileNo || "-"}</td>
                          </tr>
                          <tr>
                            <td><strong>Age / Gender</strong></td>
                            <td>:</td>
                            <td>{safeCustomer.age || "-"}{safeCustomer.gender ? ` / ${safeCustomer.gender}` : ''}</td>
                          </tr>
                          <tr>
                            <td><strong>Address</strong></td>
                            <td>:</td>
                            <td>{safeCustomer.cAddress || "-"}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Right Aligned Data */}
                    <div className="marg-patient-col marg-patient-right">
                      <table style={{ width: '100%' }}>
                        <tbody>
                          <tr>
                            <td width="55%" style={{ textAlign: 'right' }}><strong>Bill No.</strong></td>
                            <td width="5%" style={{ textAlign: 'center' }}>:</td>
                            <td style={{ textAlign: 'left' }}><strong>{safeSummary.billNo || "-"}</strong></td>
                          </tr>
                          <tr>
                            <td style={{ textAlign: 'right' }}><strong>Bill Date</strong></td>
                            <td style={{ textAlign: 'center' }}>:</td>
                            <td style={{ textAlign: 'left' }}>{receiptDateStr}</td>
                          </tr>
                          <tr>
                            <td style={{ textAlign: 'right' }}><strong>Time</strong></td>
                            <td style={{ textAlign: 'center' }}>:</td>
                            <td style={{ textAlign: 'left' }}>{receiptTimeStr}</td>
                          </tr>
                          <tr>
                            <td style={{ textAlign: 'right' }}><strong>UHID</strong></td>
                            <td style={{ textAlign: 'center' }}>:</td>
                          <td style={{ textAlign: 'left' }}>
     {safeCustomer.uhid || safeCustomer.UHID || safeCustomer.uhidNo || safeCustomer.uhiD_No || safeCustomer.patientId || "-"}
  </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* --- Bill Items Table --- */}
                  <div className="marg-table-container">
                    
                    <div className="marg-table-header">
                      <div className="marg-col-sr">S.No.</div>
                      <div className="marg-col-desc">Particulars / Services</div>
                      <div className="marg-col-qty">Qty</div>
                      <div className="marg-col-rate">Rate (Rs.)</div>
                      <div className="marg-col-amt">Amount (Rs.)</div>
                    </div>

                    {/* ✅ Brought Forward (If not page 1) */}
                    {pageIndex > 0 && (
                      <div className="marg-page-total" style={{ borderTop: 'none', borderBottom: 'var(--marg-border)' }}>
                        <div className="marg-col-sr"></div>
                        <div className="marg-col-desc" style={{ textAlign: 'right' }}>Brought Forward (B/F):</div>
                        <div className="marg-col-qty"></div>
                        <div className="marg-col-rate"></div>
                        <div className="marg-col-amt">{pageData.broughtForward.toFixed(2)}</div>
                      </div>
                    )}

                    {pageData.items.length > 0 ? (
                      pageData.items.map((item) => (
                        <div className="marg-table-row" key={item.sr}>
                          <div className="marg-col-sr">{item.sr}</div>
                          <div className="marg-col-desc">{item.description}</div>
                          <div className="marg-col-qty">{item.qty}</div>
                          <div className="marg-col-rate">{item.rate.toFixed(2)}</div>
                          <div className="marg-col-amt">{item.amount.toFixed(2)}</div>
                        </div>
                      ))
                    ) : (
                      <div className="marg-table-row" style={{ justifyContent: 'center', padding: '20px' }}>
                        No items billed.
                      </div>
                    )}

                    {/* ✅ Empty filler to stretch vertical lines to the bottom safely */}
                    <div className="marg-table-filler">
                      <div className="marg-col-sr"></div>
                      <div className="marg-col-desc"></div>
                      <div className="marg-col-qty"></div>
                      <div className="marg-col-rate"></div>
                      <div className="marg-col-amt"></div>
                    </div>
                  </div>

                  {/* ✅ Page Total / Carried Forward */}
                  {pages.length > 1 && (
                    <div className="marg-page-total" style={{ borderTop: 'none' }}>
                      <div className="marg-col-sr"></div>
                      <div className="marg-col-desc" style={{ textAlign: 'right' }}>
                        {pageIndex < pages.length - 1 ? "Carried Forward (C/F):" : "Total of Items:"}
                      </div>
                      <div className="marg-col-qty"></div>
                      <div className="marg-col-rate"></div>
                      <div className="marg-col-amt">{pageData.carriedForward.toFixed(2)}</div>
                    </div>
                  )}

                  {/* --- Summary & Footer (ONLY ON LAST PAGE) --- */}
                  {pageIndex === pages.length - 1 ? (
                    <>
                      <div className="marg-footer-grid">
                        <div className="marg-words-box">
                          <div>
                            <p style={{ margin: "0 0 5px 0", fontSize: "11px" }}><strong>Total Amount in Words:</strong></p>
                            <p style={{ margin: "0", fontSize: "13px", fontWeight: "bold", textTransform: "capitalize" }}>
                              Rupees {numberToWords(receivedAmount > 0 ? receivedAmount : payableAmount)} Only
                            </p>
                          </div>
                          <div style={{ marginTop: "15px", fontSize: "10px", color: "#444" }}>
                            <p style={{ margin: "0" }}><strong>Terms & Conditions / Disclaimer:</strong></p>
                            <ul style={{ paddingLeft: "15px", margin: "2px 0 0 0" }}>
                              <li>Items once sold will not be taken back.</li>
                              <li>All disputes are subject to local jurisdiction.</li>
                              <li>Generated By: {safeSummary.createdBy || "System"}</li>
                            </ul>
                          </div>
                        </div>
                        
                        <div className="marg-calc-box">
                          <table className="marg-calc-table">
                            <tbody>
                              <tr>
                                <td>Total Amount</td>
                                <td style={{ textAlign: "right", fontWeight: "bold" }}>{totalAmount.toFixed(2)}</td>
                              </tr>
                              {discountAmount > 0 && (
                                <tr>
                                  <td>Discount</td>
                                  <td style={{ textAlign: "right" }}>- {discountAmount.toFixed(2)}</td>
                                </tr>
                              )}
                              <tr className="marg-highlight-row">
                                <td><strong>Net Payable</strong></td>
                                <td style={{ textAlign: "right", fontWeight: "bold", fontSize: "14px" }}>{payableAmount.toFixed(2)}</td>
                              </tr>
                              <tr>
                                <td>Paid Amount</td>
                                <td style={{ textAlign: "right" }}>- {receivedAmount.toFixed(2)}</td>
                              </tr>
                              <tr style={{ borderTop: "var(--marg-border-thick)" }}>
                                <td><strong>Balance Due</strong></td>
                                <td style={{ textAlign: "right", fontWeight: "bold", fontSize: "14px" }}>{dueAmount.toFixed(2)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Signatures */}
                      <div className="marg-signature-box">
                        <div style={{ textAlign: "center", width: "30%", display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                          <div className="marg-sig-line">Patient / Attendant</div>
                        </div>
                        <div style={{ textAlign: "center", width: "30%", display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                          <div style={{ fontSize: '11px', marginBottom: '2px' }}>{safeSummary.createdBy || "Admin"}</div>
                          <div className="marg-sig-line">Prepared By</div>
                        </div>
                        <div style={{ textAlign: "center", width: "30%", display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                          <div style={{ fontSize: '11px', marginBottom: '2px' }}>{safeSummary.disAuthority || "Auth Signatory"}</div>
                          <div className="marg-sig-line">Hospital Authority</div>
                        </div>
                      </div>
                    </>
                  ) : (
                    /* Continued text for pages other than the last one */
                    <div style={{ padding: "10px", textAlign: "right", fontWeight: "bold", fontSize: "12px", fontStyle: "italic", borderTop: "var(--marg-border-thick)" }}>
                      Continued to next page...
                    </div>
                  )}

                </div>
              ))}
            </div>

            {/* --- Action Buttons (Hidden in Print) --- */}
            <div className="no-print d-flex justify-content-end gap-2 mt-2 pt-2 border-top bg-light p-3 rounded-bottom">
              <button 
                  className={`btn btn-sm ${showHeader ? "btn-outline-secondary" : "btn-warning"} fw-medium px-3`} 
                  onClick={handleToggleHeader}
              >
                <i className={`bi ${showHeader ? "bi-eye-slash" : "bi-eye"} me-2`}></i>
                {showHeader ? "Use Letterhead (Hide Header)" : "Show Header"}
              </button>
              <button className="btn btn-secondary btn-sm px-4" onClick={onClose}>Close</button>
              <button className="btn btn-primary btn-sm px-4 fw-bold" onClick={handlePrint} style={{ background: 'var(--theme-color, #0088cc)', borderColor: 'var(--theme-color, #0088cc)' }}>
                  <i className="bi bi-printer me-2"></i> Print Bill
              </button>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default CustomerBillingReceipt;